"use client";

import Image from "@/components/CdnImage";
import { type ComponentProps, type ReactEventHandler, useEffect, useState } from "react";

// A freshly generated/revised preview can 404 for a few seconds: R2's PUT is
// acknowledged before the object is readable through the public Cloudflare
// domain, and the browser then caches that 404 for the page session. This
// wrapper retries on error with a cache-busting query param so the image
// appears once propagation catches up, without a manual refresh.
const RETRY_DELAYS_MS = [1000, 2000, 4000];

type RetryImageProps = ComponentProps<typeof Image> & { src: string };

export function RetryImage({ src, onError, ...rest }: RetryImageProps) {
  const [attempt, setAttempt] = useState(0);

  // Reset retries whenever the underlying source changes (e.g. new revision).
  useEffect(() => {
    setAttempt(0);
  }, [src]);

  const resolvedSrc =
    attempt === 0 ? src : `${src}${src.includes("?") ? "&" : "?"}retry=${attempt}`;

  return (
    <Image
      {...rest}
      src={resolvedSrc}
      onError={((event) => {
        if (attempt < RETRY_DELAYS_MS.length) {
          const next = attempt + 1;
          window.setTimeout(() => setAttempt(next), RETRY_DELAYS_MS[attempt]);
        }
        onError?.(event);
      }) as ReactEventHandler<HTMLImageElement>}
    />
  );
}
